import React, { useState } from 'react';
import { X } from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

interface MultiSelectTagsProps {
  label: string;
  options: string[];
  selectedTags: string[];
  onChange: (tags: string[]) => void;
  placeholder?: string;
  maxTags?: number;
}

export function MultiSelectTags({
  label,
  options,
  selectedTags,
  onChange,
  placeholder = 'Search and select...',
  maxTags = 10
}: MultiSelectTagsProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const filteredOptions = options.filter(option =>
    option.toLowerCase().includes(searchTerm.toLowerCase()) &&
    !selectedTags.includes(option)
  );

  const handleAddTag = (tag: string) => {
    if (selectedTags.length < maxTags && !selectedTags.includes(tag)) {
      onChange([...selectedTags, tag]);
      setSearchTerm('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    onChange(selectedTags.filter(tag => tag !== tagToRemove));
  };

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      
      {/* Selected Tags */}
      {selectedTags.length > 0 && (
        <div className="flex flex-wrap gap-2 p-2 bg-gray-50 rounded-lg">
          {selectedTags.map((tag, index) => (
            <Badge
              key={index}
              variant="secondary"
              className="flex items-center gap-1 px-3 py-1"
            >
              {tag}
              <button
                type="button"
                onClick={() => handleRemoveTag(tag)}
                className="ml-1 hover:bg-gray-200 rounded-full p-0.5"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}

      {/* Input and Dropdown */}
      <div className="relative">
        <Input
          type="text"
          placeholder={placeholder}
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          className="w-full"
        />

        {isOpen && searchTerm && filteredOptions.length > 0 && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
            {filteredOptions.slice(0, 20).map((option, index) => (
              <button
                key={index}
                type="button"
                onClick={() => {
                  handleAddTag(option);
                  setIsOpen(false);
                }}
                className="w-full text-left px-4 py-2 hover:bg-gray-100 transition-colors text-sm"
              >
                {option}
              </button>
            ))}
          </div>
        )}
      </div>

      {selectedTags.length >= maxTags && (
        <p className="text-xs text-amber-600">
          Maximum {maxTags} tags allowed
        </p>
      )}
      
      {selectedTags.length === 0 && (
        <p className="text-xs text-gray-500">
          Select up to {maxTags} {label.toLowerCase()}
        </p>
      )}
    </div>
  );
}
